// Base API configuration and utilities

const API_BASE_URL =
  import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321'

// Supabase publishable key for unauthenticated requests
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY

interface RequestOptions extends RequestInit {
  timeout?: number
}

class ApiError extends Error {
  status: number
  statusText: string

  constructor(status: number, statusText: string, message?: string) {
    super(message || `API Error: ${status} ${statusText}`)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
  }
}

export class ApiClient {
  private baseURL: string
  private defaultTimeout: number
  private authToken: string | null = null
  private tokenProvider?: () => string | null
  // Requests block on this promise before firing. Default-resolved so the
  // client is usable without an auth bootstrap (tests, unauthenticated SSR-ish
  // paths). Real callers set it via setAuthReady so the first request waits
  // for the persisted session to load instead of racing it.
  private authReady: Promise<void> = Promise.resolve()

  constructor(baseURL: string = API_BASE_URL, timeout: number = 10000) {
    this.baseURL = baseURL
    this.defaultTimeout = timeout
  }

  /**
   * Set a static auth token
   */
  setAuthToken(token: string | null): void {
    this.authToken = token
  }

  /**
   * Set a function that provides the current auth token
   * Useful for dynamic token retrieval (e.g., from localStorage, context, etc.)
   */
  setTokenProvider(provider: () => string | null): void {
    this.tokenProvider = provider
  }

  /**
   * Gate requests on auth hydration. Every subsequent request awaits this
   * promise before sending, so the Authorization header reflects the actual
   * session state instead of whatever was in memory at component-mount time.
   */
  setAuthReady(ready: Promise<void>): void {
    this.authReady = ready
  }

  /**
   * Get the current auth token
   */
  private getAuthToken(): string | null {
    if (this.tokenProvider) {
      return this.tokenProvider()
    }
    return this.authToken
  }

  /**
   * Check if a token is valid (not expired)
   */
  private isTokenValid(token: string): boolean {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return false

      const payload = JSON.parse(atob(parts[1]))
      const now = Math.floor(Date.now() / 1000)

      // Add 5 minute buffer to account for clock skew
      return payload.exp > now + 300
    } catch (error) {
      console.error('Token validation failed:', error)
      return false
    }
  }

  /**
   * Get a valid auth token, checking for expiration
   */
  private getValidAuthToken(): string | null {
    const token = this.getAuthToken()

    if (!token) {
      return null
    }

    if (!this.isTokenValid(token)) {
      console.warn('Token is expired or invalid, clearing it')
      this.setAuthToken(null)
      return null
    }

    return token
  }

  /**
   * Build authorization headers. The publishable key always goes in apikey
   * (Supabase gateway recognizes it there). Authorization is reserved for
   * user JWTs — publishable keys are opaque tokens, not JWTs, and verify_jwt
   * gateways reject them in Bearer.
   */
  private buildAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {}
    if (SUPABASE_PUBLISHABLE_KEY) {
      headers.apikey = SUPABASE_PUBLISHABLE_KEY
    }
    const token = this.getValidAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
    return headers
  }

  /**
   * Handle response errors and parse successful responses
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        this.setAuthToken(null)
        window.dispatchEvent(
          new CustomEvent('auth:error', {
            detail: {
              status: response.status,
              statusText: response.statusText,
            },
          })
        )
        throw new ApiError(
          response.status,
          response.statusText,
          'Authentication failed - please sign in again'
        )
      }
      throw new ApiError(
        response.status,
        response.statusText,
        await response.text().catch(() => undefined)
      )
    }

    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    }
    return (await response.text()) as T
  }

  /**
   * Handle fetch errors
   */
  private handleFetchError(error: unknown): never {
    if (error instanceof ApiError) {
      throw error
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError(408, 'Request Timeout', 'Request timed out')
    }
    throw new ApiError(
      0,
      'Network Error',
      error instanceof Error ? error.message : 'Unknown error'
    )
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { timeout = this.defaultTimeout, ...fetchOptions } = options

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      await this.authReady
      const url = `${this.baseURL}${endpoint}`

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...this.buildAuthHeaders(),
        ...(fetchOptions.headers as Record<string, string>),
      }

      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
        headers,
      })

      clearTimeout(timeoutId)
      return await this.handleResponse<T>(response)
    } catch (error) {
      clearTimeout(timeoutId)
      return this.handleFetchError(error)
    }
  }

  async get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  /**
   * POST FormData (e.g. multipart/form-data). Do not set Content-Type so the
   * browser sets it with the correct boundary.
   */
  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: RequestOptions
  ): Promise<T> {
    const { timeout = this.defaultTimeout, ...fetchOptions } = options ?? {}

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      await this.authReady
      const url = `${this.baseURL}${endpoint}`

      const headers: Record<string, string> = {
        ...this.buildAuthHeaders(),
        ...(fetchOptions.headers as Record<string, string>),
      }

      const response = await fetch(url, {
        ...fetchOptions,
        method: 'POST',
        body: formData,
        signal: controller.signal,
        headers,
      })

      clearTimeout(timeoutId)
      return await this.handleResponse<T>(response)
    } catch (error) {
      clearTimeout(timeoutId)
      return this.handleFetchError(error)
    }
  }
}

// Default API client instance
export const apiClient = new ApiClient()

export { ApiError }
